import { UserAdminFormFactoryService } from './user-admin-form-factory.service';
import { AuthService } from 'src/app/auth/auth.service';
import { EgpSeason, EgpService } from 'src/app/egp/egp.service';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
  untracked,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { environment } from 'src/environments/environment';

import {
  createEgpFormControlName,
  createEgpSummerFormControlName,
  createEgwFormControlName,
  createEgwGmaFormControlName,
  createEgwSummerFormControlNames,
  createEopFormControlName,
  createEppFormControlName,
  DEFAULT_CONTINGENTS,
  DELIVERY_CONTINGENT_TYPES,
  EMPTY_CONTINGENTS,
  findAreaName,
  getShowUsers,
  getUserName,
  hasUserAccess,
  INITIAL_PRODUCT_LIST,
  isUserAdmin,
  isUserAllowedToAccessAllProducts,
  PRODUCT_LIST_KEYS,
  sortByArea,
  sortProductsByStationName,
  sortStationsByName,
  toEppResolutionKey,
  USER_PRODUCT_TYPES,
  USER_SECTIONS,
  userActiveProductIds,
  UserSection,
} from '../constants/constants';
import {
  USER_ADMIN_GMA,
  UserAdminGmaWebcamValue,
} from '../constants/user-admin-contract.constants';
import { DataConfig } from '../models/data-config.interface';
import { DeliveryTargetControl } from '../models/delivery-target-control.interface';
import { SaveGroup } from '../models/save-group.interface';
import { ShowAllProductsSelected } from '../models/show-all-products-selected.interface';
import { ProductListType } from '../types/product-list-type.type';
import rg4js from 'raygun4js';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isNil } from 'src/utils/utils';

export interface SectionProductConfig {
  product: any[] | null;
  [key: string]: any;
}

export interface UserMetadata {
  [sectionName: string]: SectionProductConfig | any;
}

export interface UserData {
  metadata?: UserMetadata | null;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly egpService = inject(EgpService);
  private readonly injector = inject(Injector);
  private readonly formFactory = inject(UserAdminFormFactoryService);

  private readonly urlBff = `${environment.url.bff}/`;
  private readonly urlSendMan = environment.url.sendMan;
  private readonly urlUserAdmin = environment.url.userAdmin;

  private readonly obsProductViewUserData: BehaviorSubject<any>;

  customerDocument = computed(() => this.auth.getCustomerDocument());
  userList = signal([]);

  userData = signal<UserData>({});

  obsUserList: BehaviorSubject<any[]>;
  productViewUserData = signal({});
  customerInfo = null;
  customerMetaData = {};

  loadingUserList = signal(false);
  editView = signal(false);

  productList = signal(INITIAL_PRODUCT_LIST);

  showUsers = signal({
    online: {},
    app: {},
    delivery: {},
  });

  contingents = { ...EMPTY_CONTINGENTS };
  usedContingents = signal({ ...EMPTY_CONTINGENTS });

  eppResolutions = signal([]);
  eppParametersets = signal([]);

  regionFormGroup: UntypedFormGroup = new UntypedFormGroup({
    locations: new UntypedFormControl(''),
    localFeatures: new UntypedFormControl(''),
    elevationMin: new UntypedFormControl(null),
    elevationMax: new UntypedFormControl(null),
    bridges: new UntypedFormControl('wenige'),

    hasGma: new UntypedFormControl('nein'),
    gmaList: new UntypedFormArray([]),
  });

  createGmaRow(
    coordinates = '',
    manufacturer = '',
    webcam: UserAdminGmaWebcamValue = USER_ADMIN_GMA.defaultWebcamValue,
    productKey = ''
  ): UntypedFormGroup {
    return this.formFactory.createGmaRow(
      coordinates,
      manufacturer,
      webcam,
      productKey
    );
  }

  contactInfoFormGroup: UntypedFormGroup = new UntypedFormGroup({
    contacts: new UntypedFormArray([]),
    billingCompanyName: new UntypedFormControl(''),
    billingStreet: new UntypedFormControl(''),
    billingZip: new UntypedFormControl(''),
    billingCity: new UntypedFormControl(''),
    billingEmail: new UntypedFormControl(''),
  });

  createContactRow(name = '', email = '', phone = ''): UntypedFormGroup {
    return this.formFactory.createContactRow(name, email, phone);
  }
  contactFormGroup: UntypedFormGroup;

  deliveryTargetControl: DeliveryTargetControl = {
    email: new UntypedFormControl(null, Validators.email),
    sms: new UntypedFormControl(
      null,
      Validators.pattern(/^(\+|0)[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)
    ),
    fax: new UntypedFormControl(
      null,
      Validators.pattern(/^(\+|0)[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)
    ),
  };
  formGroupStammdaten: UntypedFormGroup;
  formGroupWebsite: UntypedFormGroup;
  formGroupDelivery: UntypedFormGroup;
  formGroupApp: UntypedFormGroup;

  showAllProductsSelected = signal<ShowAllProductsSelected>({
    online: true,
    app: true,
  });

  areAllProductsSelected = computed(() => {
    const metadata = this.userData()?.['metadata'];
    const showAllProductsSelected = this.showAllProductsSelected();

    const sections = Object.keys(showAllProductsSelected);

    const result: ShowAllProductsSelected = { online: true, app: true };

    for (const section of sections) {
      const product = metadata?.[section]?.product;
      if (Array.isArray(product)) {
        result[section] = false;
      }
    }

    return result;
  });

  constructor() {
    this.obsUserList = new BehaviorSubject<any[]>(this.userList());
    this.obsProductViewUserData = new BehaviorSubject<any>(
      this.productViewUserData()
    );
  }

  getShowUsers(section: string, id: string): boolean {
    return getShowUsers(section, id, this.showUsers());
  }

  setShowUsers(section: string, id: string, value: boolean): void {
    this.showUsers.update(showUsers => {
      return {
        ...showUsers,
        [section]: { ...showUsers[section], [id]: value },
      };
    });
  }

  getProductViewUserData(): Observable<any> {
    return this.obsProductViewUserData.asObservable();
  }

  public getUserList(customerId): Observable<any> {
    return this.http
      .get<any[]>(this.getCustomerUsersUrl(customerId))
      .pipe(catchError(this.handleHttpError));
  }

  public deleteUser(userData): Observable<any> {
    return this.http
      .delete(this.getUserManagementUrl(userData['id']))
      .pipe(catchError(this.handleHttpError));
  }

  public modifyUser(userData): Observable<any> {
    return this.http
      .put(this.getUserManagementUrl(userData['id']), userData)
      .pipe(catchError(this.handleHttpError));
  }

  public createUser(userData): Observable<any> {
    const url = `${this.urlUserAdmin}/user/1/create`;

    return this.http.post(url, userData).pipe(
      catchError((error: HttpErrorResponse) => {
        const message =
          error.error?.['message'] ?? `${error.status} ${error.statusText}`;
        return throwError(() => new Error(message));
      })
    );
  }

  public getUserData(userId): Observable<any> {
    return this.http
      .get<any[]>(this.getUserManagementUrl(userId))
      .pipe(catchError(this.handleHttpError));
  }

  public userAlreadyExists(email: string): Observable<any> {
    const url = `${this.urlUserAdmin}/user/1/check/${email}`;
    return this.http.get(url);
  }

  public getAddressGroups(customerId): Observable<any> {
    const url = `${this.urlSendMan}addressgroup/search/customer/${customerId}`;

    return this.http.get(url).pipe(catchError(this.handleHttpError));
  }

  public getConfig(product: string, productId: string): Observable<DataConfig> {
    const url = `${this.urlSendMan}config/search/product/${productId}`;
    return this.http.get<DataConfig>(url);
  }

  public saveAddressGroup(addressGroup: any): Observable<SaveGroup> {
    const action = addressGroup.id ? `update/${addressGroup.id}` : 'create';
    const url = `${this.urlSendMan}addressgroup/${action}`;

    return this.http
      .post<SaveGroup>(url, addressGroup)
      .pipe(catchError(this.handleHttpError));
  }

  private getCustomerUsersUrl(customerId): string {
    return `${this.urlUserAdmin}/customer/1/user/${customerId}`;
  }

  private getUserManagementUrl(userId): string {
    return `${this.urlUserAdmin}/user/1/management/${userId}`;
  }

  private handleHttpError(
    error: Response | HttpErrorResponse | any
  ): Observable<never> {
    const message = error?.message || `${error?.status} ${error?.statusText}`;
    return throwError(() => new Error(message));
  }

  sendChangesAsFeedback(changes): Observable<any> {
    const url = `${this.urlBff}feedback/create`;
    const body = JSON.stringify(changes);

    return this.http
      .post(url, body)
      .pipe(map(this.extractData), catchError(this.handleFeedbackError));
  }

  private extractData(res): any {
    return res.id || {};
  }

  private handleFeedbackError(error: Response | any): Observable<never> {
    return throwError(() => error.message || error);
  }

  public getUsers(): void {
    this.loadingUserList.set(true);

    this.getUserList(this.customerDocument().customerId).subscribe({
      next: users => this.handleUserListLoaded(users),
      error: error => {
        this.loadingUserList.set(false);
      },
    });
  }

  private handleUserListLoaded(users: any[]): void {
    const userList = users.map(user => ({
      ...user,
      name: this.getUserName(user),
    }));

    this.userList.set(userList);
    this.obsUserList.next(userList);
    this.extractUserDataForProductList();
    this.loadingUserList.set(false);
  }

  public getUserName(userData): string {
    return getUserName(userData);
  }

  extractUserDataForProductList(): void {
    const productViewUserData: any = this.createEmptyProductViewUserData();
    const usedContingents = this.createUsedContingentsAccumulator();

    for (const user of this.userList()) {
      const metadata = user?.metadata;

      for (const section of USER_SECTIONS) {
        const sectionMetadata = metadata?.[section];

        if (!this.isUserActive(user) || !this.isUserActive(user, section)) {
          continue;
        }

        const hasProduct =
          sectionMetadata?.product !== undefined &&
          sectionMetadata?.product !== null;

        if (!hasProduct) {
          this.countUserWithoutProduct(section, usedContingents);
          continue;
        }

        if (section === 'delivery') {
          this.collectDeliveryContingents(sectionMetadata, usedContingents);
          continue;
        }

        usedContingents[section] += 1;
        this.collectProductViewUsers(
          section,
          sectionMetadata,
          user,
          productViewUserData
        );
      }
    }

    this.productViewUserData.set({ ...productViewUserData });
    this.usedContingents.set({
      online: usedContingents.online,
      app: usedContingents.app,
      sms: usedContingents.sms.size,
      fax: usedContingents.fax.size,
    });

    this.userData.set({});
    this.obsProductViewUserData.next(this.productViewUserData());
  }

  private createEmptyProductViewUserData(): any {
    return {
      online: {
        usersWithFullAccess: [],
      },
      app: {
        usersWithFullAccess: [],
      },
      delivery: {},
    };
  }

  private createUsedContingentsAccumulator(): any {
    return {
      online: 0,
      app: 0,
      sms: new Set<string>(),
      fax: new Set<string>(),
    };
  }

  private countUserWithoutProduct(
    section: UserSection,
    usedContingents: any
  ): void {
    if (section === 'online' || section === 'app') {
      usedContingents[section] += 1;
    }
  }

  private collectDeliveryContingents(
    sectionMetadata: any,
    usedContingents: any
  ): void {
    const targets = sectionMetadata?.target ?? {};

    for (const deliveryType of DELIVERY_CONTINGENT_TYPES) {
      const deliveryTargets = targets?.[deliveryType] ?? [];

      for (const target of deliveryTargets) {
        usedContingents[deliveryType].add(target);
      }
    }
  }

  private collectProductViewUsers(
    section: Exclude<UserSection, 'delivery'>,
    sectionMetadata: any,
    user: any,
    productViewUserData: any
  ): void {
    const productIds = this.userActiveProductIds(section, user);

    for (const productId of productIds) {
      const index = this.getUserProductIndex(productId, section, user);
      const product = sectionMetadata.product?.[index];

      if (!product) {
        continue;
      }

      if (product.station) {
        productViewUserData[section][productId] ??= {};

        for (const stationId of product.station) {
          productViewUserData[section][productId][stationId] ??= [];
          productViewUserData[section][productId][stationId].push(user.id);
        }

        continue;
      }

      productViewUserData[section][productId] ??= [];
      productViewUserData[section][productId].push(user.id);
    }
  }

  getUserProductIndex(productId, section: string, userData: any): number {
    if (userData?.metadata?.hasOwnProperty(section)) {
      for (let i = 0; i < userData.metadata[section]['product'].length; i++) {
        if (userData.metadata[section]['product'][i]['id'] === productId) {
          if (userData.metadata[section]['product'][i]['active']) {
            return i;
          }
        }
      }
    }
    return null;
  }

  public isUserActive(userData, section = null): boolean {
    const metadata = userData?.metadata;

    if (!metadata) {
      return true;
    }

    if (!section) {
      return metadata.active !== false;
    }

    const sectionMetadata = metadata?.[section];

    if (!sectionMetadata) {
      return true;
    }

    if (
      sectionMetadata.active !== undefined &&
      sectionMetadata.active !== null
    ) {
      return sectionMetadata.active !== false;
    }

    return section !== 'delivery' && section !== 'app';
  }

  userActiveProductIds(section, userData, options = {}): number[] {
    return userActiveProductIds(section, userData, options);
  }

  async extractProducts(): Promise<void> {
    const customerDocument = this.customerDocument();

    const egpAreas = await this.egpService.getEgpRegionsOfCustomer(
      customerDocument,
      EgpSeason.WINTER,
      true,
      true
    );

    const summerEgpAreas = await this.egpService.getEgpRegionsOfCustomer(
      customerDocument,
      EgpSeason.SUMMER,
      true,
      true
    );

    this.extractEopProducts(customerDocument);
    this.extractEppProducts(customerDocument);
    this.extractEgpProducts(customerDocument, egpAreas);
    this.extractEgpSummerProducts(customerDocument, summerEgpAreas);
    this.extractEgwProducts(customerDocument, egpAreas);
    this.extractEgwSummerProducts(customerDocument);
    this.extractSmwProducts(customerDocument);
    this.extractRadarProducts(customerDocument);
    this.extractMarwisProducts(customerDocument);
    this.extractRouteProducts(customerDocument);
    this.extractPigeonProducts(customerDocument);
    this.extractTrafficLightProducts(customerDocument);
    this.extractTrendProducts(customerDocument);
    this.extractEgwGmaProducts(customerDocument);
  }

  private extractEopProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.eop;
    const eopProducts = {};

    for (const eopProduct of customerDocument.eopProducts) {
      if (!this.isProductVisible(productType, eopProduct)) {
        continue;
      }

      const eopType = eopProduct.eopType;
      const currentStations = sortStationsByName([...eopProduct.stations]);

      eopProducts[eopType] ??= [];

      for (const station of currentStations) {
        eopProducts[eopType].push({
          id: eopProduct.id,
          station,
          eopType,
          outOfSeason: this.isProductOutOfSeason(productType, eopProduct),
          productData: eopProduct,
          formControlName: createEopFormControlName(
            eopType,
            eopProduct.id,
            station.stationId
          ),
        });
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.eop, eopProducts);
  }

  private extractEppProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.epp;
    const eppProducts = {};

    this.eppParametersets.set([]);
    this.eppResolutions.set([]);

    for (const eppProduct of customerDocument.eppProducts) {
      if (!this.isProductVisible(productType, eppProduct)) {
        continue;
      }

      for (const parameterset of eppProduct.parameter_sets) {
        this.addEppParameterset(parameterset);

        for (const resolution of eppProduct.resolutions) {
          this.addEppResolutionIfMissing(resolution);

          const resolutionKey = toEppResolutionKey(resolution);
          const currentStations = sortStationsByName([...eppProduct.stations]);

          eppProducts[parameterset] ??= {};
          eppProducts[parameterset][resolutionKey] ??= [];

          for (const station of currentStations) {
            eppProducts[parameterset][resolutionKey].push({
              id: eppProduct.id,
              parameterset,
              resolution,
              station,
              outOfSeason: this.isProductOutOfSeason(productType, eppProduct),
              productData: eppProduct,
              formControlName: createEppFormControlName(
                resolutionKey,
                parameterset,
                station.stationId
              ),
            });
          }
        }
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.epp, eppProducts);
  }

  private extractEgpProducts(customerDocument: any, egpAreas: any[]): void {
    const productType = USER_PRODUCT_TYPES.egp;

    const products = customerDocument.egpProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        area: findAreaName(egpAreas, product.areaId),
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
        formControlName: createEgpFormControlName(product.id),
      }))
      .sort(sortByArea);

    this.setProductList(PRODUCT_LIST_KEYS.egp, products);
  }

  private extractEgpSummerProducts(
    customerDocument: any,
    summerEgpAreas: any[]
  ): void {
    const productType = USER_PRODUCT_TYPES.egpSummer;
    const products = [];

    for (const product of customerDocument.egpSummerProducts) {
      if (!this.isProductVisible(productType, product)) {
        continue;
      }

      const egpAreaIdAsString = product.areaId.toString();

      try {
        products.push({
          id: product.id,
          area: findAreaName(summerEgpAreas, egpAreaIdAsString),
          outOfSeason: this.isProductOutOfSeason(productType, product),
          productData: product,
          formControlName: createEgpSummerFormControlName(product.id),
        });
      } catch (error) {
        rg4js('send', {
          error,
          customData: {
            egpProduct: JSON.stringify(product),
            summerEgpAreas: JSON.stringify(summerEgpAreas),
            egpAreaIdAsString,
          },
        });

        throw error;
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.egpSummer, products.sort(sortByArea));
  }

  private extractEgwProducts(customerDocument: any, egpAreas: any[]): void {
    const productType = USER_PRODUCT_TYPES.egw;

    const products = customerDocument.egwProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        area: findAreaName(egpAreas, product.areaId.toString(), true),
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
        formControlName: createEgwFormControlName(product.id),
      }))
      .sort(sortByArea);

    this.setProductList(PRODUCT_LIST_KEYS.egw, products);
  }

  private extractEgwSummerProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.egwSummer;

    const products = customerDocument.egwSummerProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        area: product['points'][0]['searchName'],
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
        ...createEgwSummerFormControlNames(product.id),
      }))
      .sort(sortByArea);

    this.setProductList(PRODUCT_LIST_KEYS.egwSummer, products);
  }

  private extractSmwProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.smw;
    const products = {
      gma: [],
      synop: [],
    };

    for (const smwProduct of customerDocument.smwProducts) {
      if (!this.isProductVisible(productType, smwProduct)) {
        continue;
      }

      const stations = sortStationsByName([...smwProduct.stations]);

      for (const station of stations) {
        const stationType = station.stationType === 'gma' ? 'gma' : 'synop';

        products[stationType].push({
          id: smwProduct.id,
          station,
          outOfSeason: this.isProductOutOfSeason(productType, smwProduct),
          productData: smwProduct,
        });
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.smw, {
      gma: sortProductsByStationName(products.gma),
      synop: sortProductsByStationName(products.synop),
    });
  }

  private extractRadarProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.radar;

    const products = customerDocument.radarProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        region: product.region === 'benelux' ? 'benelux' : 'de',
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
      }))
      .sort((a, b) => (a.region > b.region ? -1 : 1));

    this.setProductList(PRODUCT_LIST_KEYS.radar, products);
  }

  private extractMarwisProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.marwis;
    const products = [];

    for (const marwisProduct of customerDocument.marwisProducts) {
      if (!this.isProductVisible(productType, marwisProduct)) {
        continue;
      }

      const vehicles = marwisProduct.vehicles.map(vehicle => ({
        stationId: vehicle.id,
        overwriteStationName: vehicle.name,
      }));

      for (const vehicle of vehicles) {
        products.push({
          id: marwisProduct.id,
          station: vehicle,
          outOfSeason: this.isProductOutOfSeason(productType, marwisProduct),
          productData: marwisProduct,
        });
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.marwis, products);
  }

  private extractRouteProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.route;

    const products = customerDocument.routeProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
      }));

    this.setProductList(PRODUCT_LIST_KEYS.route, products);
  }

  private extractPigeonProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.pigeon;

    const products = customerDocument.pigeonProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
      }));

    this.setProductList(PRODUCT_LIST_KEYS.pigeon, products);
  }

  private extractTrafficLightProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.trafficLights;

    const products = customerDocument.trafficLightsProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
      }));

    this.setProductList(PRODUCT_LIST_KEYS.trafficLights, products);
  }

  private extractTrendProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.trend;

    const products = customerDocument.trendProducts
      .filter(product => this.isProductVisible(productType, product))
      .map(product => ({
        id: product.id,
        area: product.overwriteName,
        outOfSeason: this.isProductOutOfSeason(productType, product),
        productData: product,
      }));

    this.setProductList(PRODUCT_LIST_KEYS.trend, products);
  }

  private extractEgwGmaProducts(customerDocument: any): void {
    const productType = USER_PRODUCT_TYPES.egwGma;
    const products = [];

    for (const gmaProduct of customerDocument.egwGmaProducts) {
      if (!this.isProductVisible(productType, gmaProduct)) {
        continue;
      }

      for (const station of gmaProduct.stations) {
        products.push({
          id: gmaProduct.id,
          station,
          outOfSeason: this.isProductOutOfSeason(productType, gmaProduct),
          productData: gmaProduct,
          formControlName: createEgwGmaFormControlName(
            gmaProduct.id,
            station.stationId
          ),
        });
      }
    }

    this.setProductList(PRODUCT_LIST_KEYS.egwGma, products);
  }
  private addEppParameterset(parameterset: any): void {
    this.eppParametersets.update(eppParametersets => [
      ...eppParametersets,
      parameterset,
    ]);
  }

  private addEppResolutionIfMissing(resolution: any): void {
    const resolutionAlreadyExists = this.eppResolutions().some(
      currentResolution => currentResolution.days === resolution.days
    );

    if (resolutionAlreadyExists) {
      return;
    }

    this.eppResolutions.update(eppResolutions => [
      ...eppResolutions,
      resolution,
    ]);
  }

  private isProductVisible(productType: string, product: any): boolean {
    return this.auth.isProductActive(
      productType,
      product.id,
      false,
      true,
      true
    );
  }

  private isProductOutOfSeason(productType: string, product: any): boolean {
    return !this.auth.isProductActive(
      productType,
      product.id,
      false,
      false,
      true
    );
  }

  public getEndlessDtg(): string {
    return '2120-01-01T00:00:00Z';
  }

  hasUserAccess(section: string, userData): boolean {
    return hasUserAccess(section, userData);
  }

  isUserAdmin(userData): boolean {
    return isUserAdmin(userData);
  }

  public getCustomerMetaData(): Observable<any> {
    return this.http.get<any>(this.getCustomerMetadataUrl()).pipe(
      map(data => {
        if (data && data.hasOwnProperty('metadata')) {
          this.customerMetaData = data['metadata'];
        } else if (data && !data.hasOwnProperty('metadata')) {
          this.customerMetaData = data;
        }
        return this.customerMetaData;
      }),
      catchError(this.handleHttpError)
    );
  }

  public getCustomerInfo(): Observable<any> {
    return this.http.get<any[]>(this.getCustomerInfoUrl()).pipe(
      map(data => {
        const info = data.hasOwnProperty('info') ? data['info'] : '';
        this.customerInfo = info;
        return info;
      }),
      catchError(this.handleHttpError)
    );
  }

  public getUserContingents(): Observable<any> {
    return this.http.get<any[]>(this.getCustomerMetadataUrl()).pipe(
      map(data => {
        this.contingents = data?.['metadata']?.['contingent'] ?? {
          ...DEFAULT_CONTINGENTS,
        };
        return this.contingents;
      }),
      catchError(this.handleHttpError)
    );
  }

  public patchCustomerMetaData(node: string, values): Observable<any> {
    const customerMetadata = {
      ...this.customerMetaData,
      [node]: values,
    };

    return this.http
      .put(this.getCustomerMetadataUrl(), { metadata: customerMetadata })
      .pipe(
        map(data => {
          this.customerMetaData = customerMetadata;
          return data;
        }),
        catchError(this.handleHttpError)
      );
  }

  public setCustomerInfo(inputInfo: string): Observable<any> {
    return this.http.put(this.getCustomerInfoUrl(), { info: inputInfo }).pipe(
      map(data => {
        this.customerInfo = inputInfo;
        return data;
      }),
      catchError(this.handleHttpError)
    );
  }

  public sendContactForm(
    name: string,
    subject: string,
    text: string,
    fileBase64?: string,
    fileName?: string
  ): Observable<any> {
    const body = this.createContactFormPayload(
      name,
      subject,
      text,
      fileBase64,
      fileName
    );

    return this.http
      .post(this.getCustomerContactUrl(), body)
      .pipe(catchError(this.handleHttpError));
  }

  private createContactFormPayload(
    name: string,
    subject: string,
    text: string,
    fileBase64?: string,
    fileName?: string
  ): any {
    const body: any = {
      name,
      subject,
      text,
    };

    if (fileBase64 && fileName) {
      body.attachment = fileBase64;
      body.attachmentName = fileName;
    }

    return body;
  }

  private getCustomerMetadataUrl(): string {
    return `${this.urlUserAdmin}/customer/1/metadata/${this.customerDocument().customerId}`;
  }

  private getCustomerInfoUrl(): string {
    return `${this.urlUserAdmin}/customer/1/info/${this.customerDocument().customerId}`;
  }

  private getCustomerContactUrl(): string {
    return `${this.urlUserAdmin}/customer/1/contact/${this.customerDocument().customerId}`;
  }

  public isUserAllowedToAccessAllProducts(
    section: string,
    userData: any
  ): boolean {
    return isUserAllowedToAccessAllProducts(section, userData);
  }

  public listenToShowAllProductsSelectedChanges() {
    effect(
      () => {
        const metadata = this.userData()?.['metadata'];
        const showAllProductsSelected = this.showAllProductsSelected();
        const sections = Object.keys(showAllProductsSelected);

        untracked(() => {
          for (const section of sections) {
            const showProducts = showAllProductsSelected[section];
            if (!isNil(metadata)) {
              const metadataSection = metadata?.[section];
              if (!isNil(metadataSection)) {
                const sectionProduct = metadataSection?.['product'];
                if (!isNil(sectionProduct)) {
                  if (
                    Array.isArray(sectionProduct) &&
                    this.showAllProductsSelected()[section]
                  ) {
                    this.showAllProductsSelected.update(
                      showAllProductsSelected => ({
                        ...showAllProductsSelected,
                        [section]: false,
                      })
                    );
                  }
                }
              } else {
                this.userData.update(userData => {
                  return {
                    ...userData,
                    ['metadata']: {
                      ...userData['metadata'],
                      [section]: {},
                    },
                  };
                });
              }
            }
            if (showProducts && this.showAllProductsSelected()[section]) {
              this.showAllProductsSelected.update(showAllProductsSelected => ({
                ...showAllProductsSelected,
                [section]: false,
              }));
            }
          }
        });
      },
      { injector: this.injector }
    );
  }

  private setProductList(productListType: ProductListType, value: any) {
    this.productList.update(productList => ({
      ...productList,
      [productListType]: value,
    }));
  }
}
